import type { ReactNode } from "react";

import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

interface DisabledReasonTooltipProps {
  /** Why the control is unavailable (or what it does). Renders the child alone when empty. */
  reason?: ReactNode;
  /**
   * Whether the wrapped control is disabled. Disabled buttons swallow pointer events, so a
   * focusable wrapper owns the tooltip; enabled controls are the trigger themselves.
   */
  disabled?: boolean;
  side?: "top" | "right" | "bottom" | "left";
  /** Wrapper classes when disabled, e.g. `flex w-full` for a stretched button. */
  className?: string;
  children: ReactNode;
}

/** Explains a disabled control on hover and focus. Use instead of `title=` on buttons. */
export function DisabledReasonTooltip({
  reason,
  disabled = true,
  side,
  className,
  children,
}: DisabledReasonTooltipProps) {
  if (!reason) {
    return <>{children}</>;
  }
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {disabled ? (
          <span tabIndex={0} className={cn("inline-flex", className)}>
            {children}
          </span>
        ) : (
          children
        )}
      </TooltipTrigger>
      <TooltipContent side={side} className="max-w-xs">
        {reason}
      </TooltipContent>
    </Tooltip>
  );
}
